import { Currency } from '@sequencehq/api'
import {
  Phase as TotalsPhase,
  apiPhaseToCalculatePhaseTotalAmountPhase,
  calculateQuoteTotals,
  parseBillingSchedulePhaseDuration
} from '@sequencehq/utils'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import isObject from 'lodash/fp/isObject'
import { phasesToQuotePhases } from 'modules/Cube/communication/external/common.api.v1/adapters/common.adapters.out'
import {
  AmendmentContractValues,
  CubeReducerState
} from 'modules/Cube/domain/cube.domain.types'

type OriginalPhase = NonNullable<
  CubeReducerState['data']['quote']['amendment']
>['original']['phases'][number]

export const getAmendmentContractValues = (
  prevState: Pick<CubeReducerState, 'data'>
): AmendmentContractValues | undefined => {
  const amendment = prevState.data.quote.amendment

  if (prevState.data.quote.dealType !== 'AMENDMENT' || !amendment) {
    return
  }

  const currency = prevState.data.common.currency ?? 'USD'
  const original = getOriginalContractValue(amendment.original.phases, currency)
  const amended = calculateQuoteTotals({
    currency,
    startDate: prevState.data.common.startDate,
    phases: apiPhaseToCalculatePhaseTotalAmountPhase(
      phasesToQuotePhases(prevState.data)
    )
  }).totalDiscountedRate

  return { original, amended, adjustment: amended - original }
}

const getOriginalContractValue = (
  phases: OriginalPhase[],
  currency: Currency
): number => {
  const startDate = phases.at(0)?.startDate

  return calculateQuoteTotals({
    currency,
    startDate: startDate ? apiDatesAdapters.fromApi(startDate) : undefined,
    phases: phases.map(originalPhaseToTotalsPhase)
  }).totalDiscountedRate
}

const originalPhaseToTotalsPhase = (phase: OriginalPhase): TotalsPhase => {
  const prices = phase.prices.map((price, index) => ({
    ...price,
    id: `original-price-${index}`
  }))
  const priceIdsFor = (productIds: string[]) =>
    prices
      .filter(price => productIds.includes(price.productId))
      .map(price => price.id)
  const duration = parseBillingSchedulePhaseDuration({
    startDate: apiDatesAdapters.fromApi(phase.startDate),
    endDate: phase.endDate ? apiDatesAdapters.fromApi(phase.endDate) : undefined
  })

  return {
    prices,
    discounts: phase.discounts.map(discount => ({
      discountCalculationType: discount.discountCalculationType,
      amount: discount.amount,
      restrictToPrices: priceIdsFor(discount.productIds)
    })),
    minimums: phase.minimums.map((minimum, index) => ({
      id: `original-minimum-${index}`,
      amount: minimum.amount,
      restrictToPrices: priceIdsFor(minimum.productIds)
    })),
    duration: isObject(duration) ? duration : undefined
  }
}
